/**
 * Shared canonical time-group merging logic.
 *
 * Two time windows are considered "the same group" when either:
 *   1. **Proximity** – both boundaries differ by ≤ MAX_GROUP_MERGE_DIFF_MIN
 *      (using circular distance around the 24-hour clock), OR
 *   2. **Containment** – one window is fully inside the other
 *      (handles windows that cross midnight).
 *
 * Used by the weekly gap view and the detailed day-view download/print.
 */

const MINUTES_IN_DAY = 1440;

/** Maximum absolute difference (in minutes) between shift boundaries to merge into one group (2 hours). */
export const MAX_GROUP_MERGE_DIFF_MIN = 2 * 60;

/**
 * Minimum number of popular anchors a wide window must fully cover before it fans out across them.
 * A window covering fewer than this keeps its own group/tab (single-cover semantics).
 */
const MIN_COVERED_ANCHORS_TO_FAN_OUT = 2;

export type TimeWindow = { startMin: number; endMin: number };

/**
 * Returns the shortest distance between two clock-time minutes around the
 * 24-hour circle.  e.g. circularDiff(1430, 10) → 20  (not 1420).
 */
export function circularMinuteDiff(a: number, b: number): number {
	const raw = Math.abs(a - b);
	return Math.min(raw, MINUTES_IN_DAY - raw);
}

/** Duration of a window in minutes, handling overnight wrap. */
function windowDuration(w: TimeWindow): number {
	return w.endMin > w.startMin ? w.endMin - w.startMin : w.endMin + MINUTES_IN_DAY - w.startMin;
}

/**
 * Returns true when `inner` is fully contained within `outer`,
 * correctly handling windows that cross midnight.
 */
export function isContained(inner: TimeWindow, outer: TimeWindow): boolean {
	const outerDur = windowDuration(outer);
	const innerDur = windowDuration(inner);

	// An inner window can't be contained if it's longer than the outer.
	if (innerDur > outerDur) return false;

	// Normalize: shift both windows so that outer.startMin becomes 0.
	// This turns the outer into a simple [0, outerDur] range.
	const innerStart = (((inner.startMin - outer.startMin) % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY;
	const innerEnd = innerStart + innerDur;

	return innerStart >= 0 && innerEnd <= outerDur;
}

/** Computes overlap in minutes between two time windows, handling overnight wrap. */
export function computeWindowOverlapMinutes(a: TimeWindow, b: TimeWindow): number {
	const aSegs: [number, number][] =
		a.endMin > a.startMin
			? [[a.startMin, a.endMin]]
			: [
					[a.startMin, MINUTES_IN_DAY],
					[0, a.endMin],
				];
	const bSegs: [number, number][] =
		b.endMin > b.startMin
			? [[b.startMin, b.endMin]]
			: [
					[b.startMin, MINUTES_IN_DAY],
					[0, b.endMin],
				];
	let total = 0;
	for (const [as, ae] of aSegs) {
		for (const [bs, be] of bSegs) {
			total += Math.max(0, Math.min(ae, be) - Math.max(as, bs));
		}
	}
	return total;
}

/**
 * Returns true when `candidate` should merge into `group`
 * (proximity OR full containment).
 */
export function matchesTimeGroup(candidate: TimeWindow, group: TimeWindow): boolean {
	const withinProximity =
		circularMinuteDiff(candidate.startMin, group.startMin) <= MAX_GROUP_MERGE_DIFF_MIN &&
		circularMinuteDiff(candidate.endMin, group.endMin) <= MAX_GROUP_MERGE_DIFF_MIN;
	return withinProximity || isContained(candidate, group);
}

/**
 * Given time windows **sorted by priority** (e.g. most-common first),
 * returns only the canonical groups – i.e. the first occurrence of each
 * distinct group after merging via {@link matchesTimeGroup}.
 *
 * When `maxOverlapMinutes` is set, also rejects windows that overlap any
 * already-accepted group by more than the specified number of minutes.
 */
export function mergeIntoCanonicalGroups<T extends TimeWindow>(sortedWindows: T[], options?: { maxOverlapMinutes?: number }): T[] {
	const groups: T[] = [];
	const maxOverlap = options?.maxOverlapMinutes;
	for (const window of sortedWindows) {
		if (groups.some((g) => matchesTimeGroup(window, g))) continue;
		if (maxOverlap !== undefined && groups.some((g) => computeWindowOverlapMinutes(window, g) > maxOverlap)) continue;
		groups.push(window);
	}
	return groups;
}

/**
 * Builds a map from each window's key to its canonical group key.
 * Input must be sorted by priority (most-common first).
 * This is the key-mapping variant used by the day-view download.
 */
export function buildCanonicalKeyMap(sortedWindows: ReadonlyArray<{ key: string; startMin: number; endMin: number }>): Map<string, string> {
	const canonicalGroups: Array<{ key: string; startMin: number; endMin: number }> = [];
	const keyMap = new Map<string, string>();
	for (const { key, startMin, endMin } of sortedWindows) {
		const match = canonicalGroups.find((g) => matchesTimeGroup({ startMin, endMin }, g));
		if (match) {
			keyMap.set(key, match.key);
		} else {
			canonicalGroups.push({ key, startMin, endMin });
			keyMap.set(key, key);
		}
	}
	return keyMap;
}

type CanonicalWindow = { key: string; startMin: number; endMin: number };

/**
 * Like {@link buildCanonicalKeyMap}, but an unpopular wide window that fully CONTAINS 2+ popular
 * anchors spreads its rows across those popular anchors instead of remaining its own group.
 *
 * Model (popularity sort runs first, so input must be most-common first):
 * 1. {@link buildCanonicalKeyMap} establishes the canonical **anchors** (popular shift windows). A
 *    wide window that is *more* popular than narrower ones already absorbs them here via containment.
 * 2. An anchor that fully contains 2+ other anchors is a **spreader** — necessarily *less* popular
 *    than what it contains (a more-popular container would have absorbed them in step 1).
 * 3. A spreader's rows fan out across the **popular (non-spreader) anchors it covers**. Containment
 *    is transitive, so those popular anchors are always directly covered — a simple filter, no walk.
 *
 * Returns each input key → the list of canonical keys it lands on (a singleton in the normal case).
 */
export function buildCanonicalFanOutMap(sortedWindows: ReadonlyArray<CanonicalWindow>): Map<string, string[]> {
	const keyMap = buildCanonicalKeyMap(sortedWindows);
	const anchors = sortedWindows.filter((w) => keyMap.get(w.key) === w.key);
	const covers = (a: CanonicalWindow, o: CanonicalWindow): boolean =>
		o.key !== a.key && isContained({ startMin: o.startMin, endMin: o.endMin }, { startMin: a.startMin, endMin: a.endMin });

	// "Leaf" anchors are the popular shift groups that wide windows fan INTO — anchors that don't
	// themselves cover any other anchor. Any anchor that covers another (whether 2+ "spreader" or
	// 1 single-cover "medium") represents a rare wider shift and is NOT a valid fan-out target.
	const leaves = anchors.filter((a) => anchors.every((o) => !covers(a, o)));
	const leafKeys = new Set(leaves.map((a) => a.key));

	// Window-driven: each input window's destination is computed from ITS OWN coverage of leaves,
	// independent of what canonical bucket `buildCanonicalKeyMap` happened to assign it. This is
	// critical when two wide windows fold together by proximity — the canonical loses the bigger
	// window's identity, but each window's row destination still needs to reflect its own coverage.
	const result = new Map<string, string[]>();
	for (const w of sortedWindows) {
		const wCovers = leaves.filter((leaf) => covers(w, leaf)).map((leaf) => leaf.key);
		if (wCovers.length >= MIN_COVERED_ANCHORS_TO_FAN_OUT) {
			result.set(w.key, wCovers);
			continue;
		}
		// 0 or 1 leaf covered: fold via canonical only when canonical is itself a leaf (a real popular
		// anchor). Otherwise (canonical is a non-leaf wide anchor, or w is its own non-leaf anchor),
		// keep w's own tab to avoid bundling rows into a group they don't fully belong to.
		const canonical = keyMap.get(w.key) ?? w.key;
		result.set(w.key, leafKeys.has(canonical) ? [canonical] : [w.key]);
	}
	return result;
}
